import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Form, Modal } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaArrowsAltH, FaChevronDown, FaChevronRight, FaCopy } from 'react-icons/fa';
import { apiFetch } from '../api';
import { useAuth } from '../auth/AuthContext';
import { canManageContracts, STAGE_GROUPS } from '../auth/roles';
import {
  PageHeader,
  QuantityProgress,
  DeadlineBadge,
} from './ui';
import ContractEditModal from './ContractEditModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { formatDate } from '../utils/date';
import { formatCurrency } from '../utils/display';
import StageEditModal from './StageEditModal';
import { fetchImageAsFile } from '../utils/image';

// Статусы для каждой стадии
const STATUS_OPTIONS = {
  procurement: ['Запрос КП', 'Анализ КП', 'Заключение договора', 'Заключение спецификации'],
  production: ['Резка', 'Закалка', 'Токарка', 'Покрытие', 'Финишная обработка'],
  assembly: ['Транспортировка', 'Сборка'],
  testing: ['Согласно ПМ', 'Прикрепить график'],
  packaging: ['Комплектация', 'Распечатка документов'],
  completed: [],
  cancelled: [],
  incorrect: [],
};

const formatQuantity = (value) => {
  if (value === null || value === undefined) return '0';
  const num = Number(value);
  if (Number.isInteger(num)) {
    return num.toString();
  }
  return num.toFixed(2);
};

function ContractStages() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [editingDetail, setEditingDetail] = useState(null);
  const { user } = useAuth();
  const canEditContract = canManageContracts(user?.role);
  const [preselectedStatus, setPreselectedStatus] = useState(null);
  const [massMoveTarget, setMassMoveTarget] = useState(null);
  const [massDeleteTarget, setMassDeleteTarget] = useState(null);
  const [copyDetailData, setCopyDetailData] = useState(null);

  const refreshContract = useCallback(() => {
    return apiFetch(`/api/contracts/${contractId}/`).then((data) => {
      setContract(data);
      return data;
    });
  }, [contractId]);

  const fetchContract = useCallback(() => {
    setLoading(true);
    setError(null);
    return refreshContract()
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить договор');
      })
      .finally(() => setLoading(false));
  }, [refreshContract]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  useEffect(() => {
    if (contract) {
      document.title = `Договор ${contract.number} — Производство`;
    }
  }, [contract]);

  const groupedDetails = React.useMemo(() => {
    if (!contract?.details) return {};
    const groups = {};
    contract.details.forEach((detail) => {
      const group = detail.stage_group || 'other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(detail);
    });
    return groups;
  }, [contract?.details]);

  const toggleGroup = (groupKey) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const createDetail = async (formData, imageFile) => {
    const payload = {
      name: formData.name?.trim() || '',
      quantity: Number(formData.quantity) || 0,
      price_per_unit: Number(formData.price_per_unit) || 0,
      material: formData.material?.trim() || '',
      blank_type: formData.blank_type?.trim() || '',
      dimension_per_unit: formData.dimension_per_unit?.trim() || '',
      dimension_total: formData.dimension_total?.trim() || '',
      stage_group: formData.stage_group || 'procurement',
      status: formData.status || '',
      deadline: formData.deadline || null,
      description: formData.description || '',
      contract: parseInt(contractId, 10),
    };
    if (!payload.name) {
      alert('Введите наименование');
      return;
    }
    if (!payload.status) {
      alert('Выберите статус');
      return;
    }

    const data = new FormData();
    for (const key in payload) {
      if (payload[key] !== null && payload[key] !== undefined) {
        data.append(key, payload[key]);
      }
    }
    if (imageFile) {
      data.append('image', imageFile);
    }

    try {
      await apiFetch('/api/details/', { method: 'POST', body: data });
      setShowCreateModal(false);
      setCopyDetailData(null);
      await refreshContract();
      setOpenGroups(prev => ({ ...prev, [formData.stage_group]: true }));
    } catch (err) {
      console.error(err);
      alert('Ошибка создания');
    }
  };

  const deleteDetail = (detailId) => {
    if (!window.confirm('Удалить деталь?')) return;
    apiFetch(`/api/details/${detailId}/`, { method: 'DELETE' })
      .then(() => refreshContract())
      .catch((err) => console.error(err));
  };

  const handleMoveDetail = async (targetStage, quantity) => {
    if (!moveTarget || !targetStage) return;
    const { detailId, maxQuantity, detailData } = moveTarget;
    const qty = Number(quantity);
    const maxQty = Number(maxQuantity);
    if (qty <= 0 || qty > maxQty) {
      alert('Некорректное количество');
      return;
    }

    try {
      const newStatusOptions = STATUS_OPTIONS[targetStage] || [];
      const newStatus = newStatusOptions.length > 0 ? newStatusOptions[0] : '';
      const newDetail = {
        name: detailData.name,
        quantity: qty,
        price_per_unit: detailData.price_per_unit,
        material: detailData.material || '',
        blank_type: detailData.blank_type || '',
        dimension_per_unit: detailData.dimension_per_unit || '',
        dimension_total: detailData.dimension_total || '',
        stage_group: targetStage,
        status: newStatus,
        deadline: detailData.deadline,
        description: detailData.description || '',
        contract: parseInt(contractId, 10),
        copy_files_from: detailId,
      };
      await apiFetch('/api/details/', {
        method: 'POST',
        body: JSON.stringify(newDetail),
      });

      if (qty === maxQty) {
        await apiFetch(`/api/details/${detailId}/`, { method: 'DELETE' });
      } else {
        const remaining = maxQty - qty;
        await apiFetch(`/api/details/${detailId}/`, {
          method: 'PATCH',
          body: JSON.stringify({ quantity: remaining }),
        });
      }

      setMoveTarget(null);
      await refreshContract();
      setOpenGroups(prev => ({ ...prev, [targetStage]: true }));
    } catch (err) {
      console.error(err);
      alert('Ошибка перемещения');
    }
  };

  const handleMassMove = async (sourceGroup, targetStage) => {
    if (!sourceGroup || !targetStage) return;
    const details = groupedDetails[sourceGroup] || [];
    if (details.length === 0) return;

    try {
      for (const detail of details) {
        const newStatusOptions = STATUS_OPTIONS[targetStage] || [];
        const newStatus = newStatusOptions.length > 0 ? newStatusOptions[0] : '';
        const newDetail = {
          name: detail.name,
          quantity: detail.quantity,
          price_per_unit: detail.price_per_unit,
          material: detail.material || '',
          blank_type: detail.blank_type || '',
          dimension_per_unit: detail.dimension_per_unit || '',
          dimension_total: detail.dimension_total || '',
          stage_group: targetStage,
          status: newStatus,
          deadline: detail.deadline,
          description: detail.description || '',
          contract: parseInt(contractId, 10),
          copy_files_from: detail.id,
        };
        await apiFetch('/api/details/', {
          method: 'POST',
          body: JSON.stringify(newDetail),
        });
        await apiFetch(`/api/details/${detail.id}/`, { method: 'DELETE' });
      }
      setMassMoveTarget(null);
      await refreshContract();
      setOpenGroups(prev => ({ ...prev, [targetStage]: true }));
    } catch (err) {
      console.error(err);
      alert('Ошибка массового перемещения');
    }
  };

  const handleMassDelete = async (sourceGroup) => {
    const details = groupedDetails[sourceGroup] || [];
    if (details.length === 0) return;
    try {
      for (const detail of details) {
        await apiFetch(`/api/details/${detail.id}/`, { method: 'DELETE' });
      }
      setMassDeleteTarget(null);
      await refreshContract();
    } catch (err) {
      console.error(err);
      alert('Ошибка массового удаления');
    }
  };

  if (loading) return <div className="empty-state">Загрузка...</div>;
  if (error) return <div className="empty-state text-danger">{error}</div>;
  if (!contract) return <div className="empty-state">Договор не найден</div>;

  const getStatusOptions = (stageGroup) => STATUS_OPTIONS[stageGroup] || [];

  const DetailCard = ({ detail, onCopy }) => {
    const statusOptions = getStatusOptions(detail.stage_group);
    const currentIndex = statusOptions.indexOf(detail.status);
    const totalPrice = Number(detail.quantity) * Number(detail.price_per_unit);

    const openMoveModal = () => {
      setMoveTarget({
        detailId: detail.id,
        currentStage: detail.stage_group,
        detailName: detail.name,
        maxQuantity: detail.quantity,
        detailData: detail,
      });
    };

    const handleEdit = () => {
      setPreselectedStatus(null);
      setEditingDetail(detail);
    };

    const handleStatusClick = (status) => {
      if (status !== detail.status) {
        if (window.confirm(`Перевести статус на "${status}"?`)) {
          apiFetch(`/api/details/${detail.id}/`, {
            method: 'PATCH',
            body: JSON.stringify({ status: status }),
          })
            .then(() => refreshContract())
            .catch((err) => {
              console.error(err);
              alert('Не удалось изменить статус');
            });
        }
      }
    };

    const renderStatusButtons = () => {
      const statuses = statusOptions;
      return (
        <div className="status-buttons-group d-flex flex-wrap gap-1">
          {statuses.map((status, idx) => {
            const isCompleted = idx < currentIndex;
            const isActive = status === detail.status;
            return (
              <button
                key={status}
                type="button"
                className={`app-btn app-btn-sm ${isActive ? 'app-btn-primary' : ''} ${isCompleted ? 'app-btn-success' : ''}`}
                onClick={() => handleStatusClick(status)}
                disabled={isActive || isCompleted}
              >
                {status}
              </button>
            );
          })}
        </div>
      );
    };

    return (
      <div className="detail-card detail-card-full detail-card-with-bg">
        {detail.image && (
          <img
            src={detail.image}
            alt={detail.name}
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              objectFit: 'contain',
              display: 'block',
              marginBottom: '8px',
              borderRadius: '4px',
            }}
          />
        )}
        <div className="detail-card-body">
          <div className="detail-card-header">
            <span className="detail-card-name">{detail.name}</span>
            <span className="detail-card-quantity">× {formatQuantity(detail.quantity)}</span>
          </div>

          <div className="detail-card-badges">
            {detail.material && (
              <span className="app-badge app-badge-neutral">Материал: {detail.material}</span>
            )}
            {detail.blank_type && (
              <span className="app-badge app-badge-neutral">Заготовка: {detail.blank_type}</span>
            )}
            {detail.dimension_per_unit && (
              <span className="app-badge app-badge-neutral">1 шт: {detail.dimension_per_unit}</span>
            )}
            {detail.dimension_total && (
              <span className="app-badge app-badge-neutral">Всего: {detail.dimension_total}</span>
            )}
          </div>

          <div className="detail-card-details-grid">
            <div className="detail-card-field">
              <span className="card-label">Цена за ед.</span>
              <span className="card-value">{formatCurrency(detail.price_per_unit)}</span>
            </div>
            <div className="detail-card-field">
              <span className="card-label">Общая сумма</span>
              <span className="card-value">{formatCurrency(totalPrice)}</span>
            </div>
            <div className="detail-card-field">
              <span className="card-label">Статус</span>
              {renderStatusButtons()}
            </div>
            <div className="detail-card-field">
              <span className="card-label">Срок</span>
              <span className="card-value">{formatDate(detail.deadline)}</span>
            </div>
            {detail.description && (
              <div className="detail-card-field detail-card-description">
                <span className="card-label">Описание</span>
                <span className="card-value">{detail.description}</span>
              </div>
            )}
          </div>
          <div className="detail-card-actions">
            <button type="button" className="app-btn app-btn-sm" onClick={openMoveModal} title="Переместить">
              <FaArrowsAltH />
            </button>
            <button type="button" className="app-btn app-btn-sm" onClick={handleEdit} title="Редактировать">
              <FaEdit />
            </button>
            <button type="button" className="app-btn app-btn-sm" onClick={() => deleteDetail(detail.id)} title="Удалить">
              <FaTrash />
            </button>
            <button type="button" className="app-btn app-btn-sm" onClick={() => onCopy(detail)} title="Копировать">
              <FaCopy />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const MoveStageModal = () => {
    const [selectedStage, setSelectedStage] = useState(null);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
      if (moveTarget) {
        setSelectedStage(null);
        setQuantity(moveTarget.maxQuantity);
      }
    }, [moveTarget]);

    if (!moveTarget) return null;

    const { currentStage, detailName, maxQuantity } = moveTarget;
    const stageKeys = Object.keys(STAGE_GROUPS);

    const handleQuantityChange = (e) => {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val) && val >= 1) {
        setQuantity(Math.min(val, maxQuantity));
      } else {
        setQuantity(1);
      }
    };

    const handleSliderChange = (e) => {
      const val = parseInt(e.target.value, 10);
      setQuantity(val);
    };

    const handleConfirm = () => {
      if (!selectedStage || selectedStage === currentStage) return;
      handleMoveDetail(selectedStage, quantity);
      setMoveTarget(null);
    };

    return (
      <Modal show={!!moveTarget} onHide={() => { setMoveTarget(null); }} centered>
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>Переместить деталь</Modal.Title>
        </Modal.Header>
        <Modal.Body className="app-surface">
          <p><strong>{detailName}</strong></p>
          <p>Текущая стадия: {STAGE_GROUPS[currentStage]?.label}</p>

          <Form.Group className="mb-3">
            <Form.Label>Количество для перемещения</Form.Label>
            <div className="d-flex align-items-center gap-3">
              <Form.Control
                type="number"
                min={1}
                max={maxQuantity}
                value={quantity}
                onChange={handleQuantityChange}
                style={{ width: '80px' }}
              />
              <Form.Range
                min={1}
                max={maxQuantity}
                step={1}
                value={quantity}
                onChange={handleSliderChange}
                style={{ flex: 1 }}
              />
              <span className="text-muted small" style={{ width: '40px' }}>
                {quantity}
              </span>
            </div>
            <Form.Text className="text-muted">
              Доступно: {maxQuantity} шт.
            </Form.Text>
          </Form.Group>

          <Form.Label>Выберите новую стадию:</Form.Label>
          <div className="d-flex flex-column gap-2 align-items-stretch">
            {stageKeys.map((key) => {
              const isActive = key === currentStage;
              const isSelected = key === selectedStage;
              return (
                <button
                  key={key}
                  type="button"
                  className={`app-btn ${isSelected ? 'app-btn-primary' : ''} ${isActive ? 'app-btn-secondary' : ''}`}
                  style={{ minWidth: '80px', justifyContent: 'center' }}
                  disabled={isActive}
                  onClick={() => setSelectedStage(key)}
                >
                  {STAGE_GROUPS[key].icon} {STAGE_GROUPS[key].label}
                  {isActive && ' (текущая)'}
                  {isSelected && ' ✓'}
                </button>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer className="app-surface">
          <button type="button" className="app-btn" onClick={() => { setMoveTarget(null); }}>
            Отмена
          </button>
          <button
            type="button"
            className="app-btn app-btn-primary"
            disabled={!selectedStage || selectedStage === currentStage}
            onClick={handleConfirm}
          >
            Переместить
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  const MassMoveModal = () => {
    const [selectedStage, setSelectedStage] = useState(null);
    if (!massMoveTarget) return null;
    const { sourceGroup } = massMoveTarget;
    const stageKeys = Object.keys(STAGE_GROUPS);

    const handleConfirm = () => {
      if (!selectedStage || selectedStage === sourceGroup) return;
      if (window.confirm(`Переместить все детали из стадии "${STAGE_GROUPS[sourceGroup]?.label}" в "${STAGE_GROUPS[selectedStage]?.label}"?`)) {
        handleMassMove(sourceGroup, selectedStage);
      }
    };

    return (
      <Modal show={!!massMoveTarget} onHide={() => { setMassMoveTarget(null); }} centered>
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>Массовое перемещение</Modal.Title>
        </Modal.Header>
        <Modal.Body className="app-surface">
          <p>Переместить все детали из стадии <strong>{STAGE_GROUPS[sourceGroup]?.label}</strong> в новую стадию:</p>
          <div className="d-flex flex-column gap-2 align-items-stretch">
            {stageKeys.map((key) => {
              const isActive = key === sourceGroup;
              const isSelected = key === selectedStage;
              return (
                <button
                  key={key}
                  type="button"
                  className={`app-btn ${isSelected ? 'app-btn-primary' : ''} ${isActive ? 'app-btn-secondary' : ''}`}
                  style={{ minWidth: '80px', justifyContent: 'center' }}
                  disabled={isActive}
                  onClick={() => setSelectedStage(key)}
                >
                  {STAGE_GROUPS[key].icon} {STAGE_GROUPS[key].label}
                  {isActive && ' (текущая)'}
                  {isSelected && ' ✓'}
                </button>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer className="app-surface">
          <button type="button" className="app-btn" onClick={() => { setMassMoveTarget(null); }}>
            Отмена
          </button>
          <button
            type="button"
            className="app-btn app-btn-primary"
            disabled={!selectedStage || selectedStage === sourceGroup}
            onClick={handleConfirm}
          >
            Переместить все
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  const MassDeleteModal = () => {
    if (!massDeleteTarget) return null;
    const { sourceGroup } = massDeleteTarget;
    const groupLabel = STAGE_GROUPS[sourceGroup]?.label || sourceGroup;
    const count = groupedDetails[sourceGroup]?.length || 0;

    const handleConfirm = () => {
      if (window.confirm(`Удалить все ${count} деталей из стадии "${groupLabel}"?`)) {
        handleMassDelete(sourceGroup);
      }
    };

    return (
      <Modal show={!!massDeleteTarget} onHide={() => { setMassDeleteTarget(null); }} centered>
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>Массовое удаление</Modal.Title>
        </Modal.Header>
        <Modal.Body className="app-surface">
          <p>Вы действительно хотите удалить все <strong>{count}</strong> деталей из стадии <strong>{groupLabel}</strong>?</p>
          <p className="text-muted small">Это действие необратимо.</p>
        </Modal.Body>
        <Modal.Footer className="app-surface">
          <button type="button" className="app-btn" onClick={() => { setMassDeleteTarget(null); }}>
            Отмена
          </button>
          <button type="button" className="app-btn app-btn-danger" onClick={handleConfirm}>
            Удалить все
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  const CreateDetailModal = () => {
    const [form, setForm] = useState({
      name: '',
      quantity: '',
      price_per_unit: '',
      material: '',
      blank_type: '',
      dimension_per_unit: '',
      dimension_total: '',
      stage_group: 'procurement',
      status: '',
      deadline: '',
      description: '',
    });
    const [saving, setSaving] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    const statusOptions = getStatusOptions(form.stage_group);

    useEffect(() => {
      if (copyDetailData) {
        setForm({
          name: copyDetailData.name || '',
          quantity: copyDetailData.quantity || '',
          price_per_unit: copyDetailData.price_per_unit || '',
          material: copyDetailData.material || '',
          blank_type: copyDetailData.blank_type || '',
          dimension_per_unit: copyDetailData.dimension_per_unit || '',
          dimension_total: copyDetailData.dimension_total || '',
          stage_group: copyDetailData.stage_group || 'procurement',
          status: copyDetailData.status || '',
          deadline: copyDetailData.deadline || '',
          description: copyDetailData.description || '',
        });
        if (copyDetailData.image) {
          fetchImageAsFile(copyDetailData.image)
            .then(file => {
              setImageFile(file);
              setImagePreview(URL.createObjectURL(file));
            })
            .catch(err => {
              console.warn('Не удалось скопировать изображение детали', err);
              setImageFile(null);
              setImagePreview(null);
            });
        } else {
          setImageFile(null);
          setImagePreview(null);
        }
      } else {
        setForm({
          name: '',
          quantity: '',
          price_per_unit: '',
          material: '',
          blank_type: '',
          dimension_per_unit: '',
          dimension_total: '',
          stage_group: 'procurement',
          status: '',
          deadline: '',
          description: '',
        });
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }, [copyDetailData, fileInputRef]);

    const handleClose = () => {
      setShowCreateModal(false);
      setCopyDetailData(null);
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleChange = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      if (field === 'stage_group') {
        setForm((prev) => ({ ...prev, status: '' }));
      }
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
      }
    };

    const handleRemoveImage = () => {
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      await createDetail(form, imageFile);
      setSaving(false);
      handleClose();
    };

    const renderStageButtons = () => {
      const stageKeys = Object.keys(STAGE_GROUPS);
      return (
        <div className="d-flex flex-wrap gap-2">
          {stageKeys.map((key) => {
            const isActive = key === form.stage_group;
            return (
              <button
                key={key}
                type="button"
                className={`app-btn ${isActive ? 'app-btn-primary' : ''}`}
                onClick={() => handleChange('stage_group', key)}
                style={{ minWidth: '80px', flex: '1 0 auto' }}
              >
                {STAGE_GROUPS[key].icon} {STAGE_GROUPS[key].label}
                {isActive && ' ✓'}
              </button>
            );
          })}
        </div>
      );
    };

    const renderStatusButtons = () => {
      if (!statusOptions.length) {
        return <div className="app-text-muted small">Нет статусов для выбранной стадии</div>;
      }
      return (
        <div className="d-flex flex-wrap gap-2">
          {statusOptions.map((status) => {
            const isActive = status === form.status;
            return (
              <button
                key={status}
                type="button"
                className={`app-btn ${isActive ? 'app-btn-primary' : ''}`}
                onClick={() => handleChange('status', status)}
                style={{ minWidth: '60px', flex: '1 0 auto' }}
              >
                {status}
                {isActive && ' ✓'}
              </button>
            );
          })}
        </div>
      );
    };

    return (
      <Modal show={showCreateModal} onHide={handleClose} centered size="lg">
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>{copyDetailData ? 'Копировать деталь' : 'Добавить деталь'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="app-surface">
            <Form.Group className="mb-3">
              <Form.Label>Наименование</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                placeholder="Введите название детали"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Количество</Form.Label>
              <Form.Control
                type="number"
                step="1"
                min="0"
                value={form.quantity}
                onChange={(e) => handleChange('quantity', e.target.value)}
                placeholder="0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Цена за единицу, ₽</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={form.price_per_unit}
                onChange={(e) => handleChange('price_per_unit', e.target.value)}
                placeholder="0.00"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Материал</Form.Label>
              <Form.Control
                type="text"
                value={form.material}
                onChange={(e) => handleChange('material', e.target.value)}
                placeholder="Например: Сталь 45"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Вид заготовки</Form.Label>
              <Form.Control
                type="text"
                value={form.blank_type}
                onChange={(e) => handleChange('blank_type', e.target.value)}
                placeholder="Например: Пруток, лист, поковка"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Размер на 1 шт</Form.Label>
              <Form.Control
                type="text"
                value={form.dimension_per_unit}
                onChange={(e) => handleChange('dimension_per_unit', e.target.value)}
                placeholder="Например: 100x50x20 мм"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Размер на общее кол-во</Form.Label>
              <Form.Control
                type="text"
                value={form.dimension_total}
                onChange={(e) => handleChange('dimension_total', e.target.value)}
                placeholder="Например: 2000x1000x5 мм"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Стадия</Form.Label>
              {renderStageButtons()}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Статус</Form.Label>
              {renderStatusButtons()}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Срок</Form.Label>
              <Form.Control
                type="date"
                value={form.deadline}
                onChange={(e) => handleChange('deadline', e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Дополнительная информация"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Изображение</Form.Label>
              <div className="d-flex flex-column gap-2">
                {imagePreview && (
                  <div style={{ position: 'relative', maxWidth: '200px' }}>
                    <img
                      src={imagePreview}
                      alt="Превью"
                      style={{ width: '100%', height: 'auto', borderRadius: '6px' }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      style={{ position: 'absolute', top: '4px', right: '4px', borderRadius: '50%' }}
                      onClick={handleRemoveImage}
                    >
                      ×
                    </button>
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="form-control"
                  />
                  <small className="text-muted">Рекомендуемый размер: 400×300 px</small>
                </div>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="app-surface">
            <button type="button" className="app-btn" onClick={handleClose}>
              Отмена
            </button>
            <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
              {saving ? 'Создание...' : (copyDetailData ? 'Копировать' : 'Создать')}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  };

  const handleCopyDetail = (detail) => {
    setCopyDetailData(detail);
    setShowCreateModal(true);
  };

  return (
    <>
      <PageHeader
        title={contract.number}
        subtitle={`${contract.product_name} · ${formatDate(contract.start_date)} — ${formatDate(contract.end_date)}`}
        backLink={<Link to={`/product/${contract.product}/contracts`} className="app-btn mb-5 d-inline-flex">Назад к договорам</Link>}
        actions={
          canEditContract && (
            <>
              <button type="button" className="app-btn" onClick={() => setShowEditModal(true)}>
                <FaEdit />
              </button>
              <button type="button" className="app-btn app-btn-primary" onClick={() => { setCopyDetailData(null); setShowCreateModal(true); }}>
                <FaPlus />
              </button>
              <button type="button" className="app-btn app-btn-danger-outline" onClick={() => setShowDeleteConfirm(true)}>
                <FaTrash />
              </button>
            </>
          )
        }
      />

      <div className="stats-row mb-3">
        <div className="stat-card">
          <QuantityProgress done={contract.done_quantity || 0} total={contract.quantity || 0} />
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Ближайший срок</div>
          {contract.nearest_deadline ? (
            <DeadlineBadge deadline={formatDate(contract.nearest_deadline)} isOverdue={contract.is_overdue} />
          ) : (
            <div className="app-text-muted">—</div>
          )}
        </div>
      </div>

      <h2 className="mt-4 mb-3" style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>Детали</h2>
      <div className="details-groups accordion-container">
        {Object.keys(groupedDetails).length === 0 && <div className="app-text-muted">Нет деталей</div>}
        {Object.keys(groupedDetails).map((group) => (
          <div key={group} className="detail-group accordion-item">
            <div
              className="detail-group-header accordion-header"
              onClick={() => toggleGroup(group)}
              style={{ cursor: 'pointer' }}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <span>
                  {STAGE_GROUPS[group]?.icon} {STAGE_GROUPS[group]?.label}
                  <span className="badge bg-secondary ms-2">{groupedDetails[group].length}</span>
                </span>
                <div className="d-flex gap-2">
                  {canEditContract && groupedDetails[group]?.length > 0 && (
                    <>
                      <button
                        type="button"
                        className="app-btn app-btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMassMoveTarget({ sourceGroup: group });
                        }}
                        title="Переместить все детали из этой стадии"
                      >
                        <FaArrowsAltH />
                      </button>
                      <button
                        type="button"
                        className="app-btn app-btn-sm app-btn-danger-outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMassDeleteTarget({ sourceGroup: group });
                        }}
                        title="Удалить все детали из этой стадии"
                      >
                        <FaTrash />
                      </button>
                    </>
                  )}
                  <span>
                    {openGroups[group] ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                </div>
              </div>
            </div>
            {openGroups[group] && (
              <div className="detail-list accordion-body">
                {groupedDetails[group].map((detail) => (
                  <DetailCard key={detail.id} detail={detail} onCopy={handleCopyDetail} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <ContractEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        contract={contract}
        onSaved={() => refreshContract()}
        onDelete={() => setShowDeleteConfirm(true)}
      />

      <ConfirmDeleteModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          await apiFetch(`/api/contracts/${contractId}/`, { method: 'DELETE' });
          navigate(`/product/${contract.product}/contracts`);
        }}
        title="Удалить договор?"
        message="Будут удалены все детали, остатки и история этого договора."
        itemName={contract.number}
      />

      <MoveStageModal />
      <MassMoveModal />
      <MassDeleteModal />
      <CreateDetailModal />

      <StageEditModal
        show={!!editingDetail}
        onHide={() => { setEditingDetail(null); setPreselectedStatus(null); }}
        stage={editingDetail}
        contractId={contractId}
        onSaved={refreshContract}
        initialStatus={preselectedStatus}
      />
    </>
  );
}

export default ContractStages;